import React, { useCallback, useState } from 'react';

import { offset, shift, useFloating, useHover, useInteractions } from '@floating-ui/react';

import { MediaAttachmentStates, isUploadInflightState } from '../lib/media_attachments_store';
import { cn } from '../lib/utils';

import SpinnerIcon from '../../assets/images/spinner.svg';

import DarkTooltipPortal from './shared/DarkTooltipPortal';
import MediaAttachmentThumbnail from './shared/MediaAttachmentThumbnail';

// 'large' is the original size, kept as the default so existing callers don't shift.
// 'small' drops the md: bump — meant for tiles compact enough that a 24px chip would
// swallow the thumbnail underneath it.
const REMOVE_BUTTON_SIZE_CLASSES = {
  large: 'size-4 md:size-6',
  small: 'size-4',
};

export const clampUploadProgress = (progress) => {
  if (!Number.isFinite(progress)) return 0;
  if (progress < 0) return 0;
  if (progress > 100) return 100;
  return Math.round(progress);
};

export const getMediaAttachmentFileName = (mediaAttachment) => {
  return mediaAttachment?.metadata?.file_name || 'Attachment';
};

const MediaAttachmentUploadPreview = ({
  mediaAttachment,
  size = 'size-18',
  className = '',
  onRemove,
  showRemoveButton = false,
  removeButtonLabel = 'Remove attachment',
  removeButtonSize = 'large',
  showStatusOverlay = true,
  showTint = true,
  hideDuration = false,
  objectFit = 'contain',
  rounded = 'rounded-lg',
}) => {
  const progress = clampUploadProgress(
    mediaAttachment?.upload?.progress ?? (mediaAttachment?.upload?.state === MediaAttachmentStates.completed ? 100 : 0),
  );

  const isUploading = isUploadInflightState(mediaAttachment?.upload?.state);
  const isProcessing = mediaAttachment?.upload?.state === MediaAttachmentStates.processing;
  const isRetrying = mediaAttachment?.upload?.state === MediaAttachmentStates.retrying;
  const isError = mediaAttachment?.upload?.state === MediaAttachmentStates.error;

  // Floating UI tooltip for filename - centered above image
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: 'top',
    middleware: [offset(10), shift()],
  });
  const hover = useHover(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const handleRemove = useCallback(
    (event) => {
      if (!onRemove) return;
      event.preventDefault();
      event.stopPropagation();
      onRemove(mediaAttachment);
    },
    [onRemove, mediaAttachment],
  );

  const fileName = getMediaAttachmentFileName(mediaAttachment);

  return (
    <>
      <div
        ref={refs.setReference}
        className={`relative ${rounded}${isRetrying ? ' outline outline-1 outline-yellow-400' : ''}`}
        {...getReferenceProps()}
      >
        <MediaAttachmentThumbnail
          hideDuration={hideDuration}
          mediaAttachment={mediaAttachment}
          size={size}
          className={className}
          showTint={showTint}
          alt={fileName}
          objectFit={objectFit}
        />

        {showStatusOverlay && (
          <>
            {isUploading && (
              <div className={`absolute inset-0 flex items-center justify-center ${rounded} bg-black/50 text-xs text-white`}>
                {progress}%
              </div>
            )}
            {isProcessing && (
              <div className={`absolute inset-0 flex items-center justify-center ${rounded} bg-black/50 text-xs text-white`}>
                <SpinnerIcon className="size-4 animate-spin" />
              </div>
            )}
            {isError && (
              <div
                className={`absolute inset-0 flex items-center justify-center ${rounded} bg-black/50 px-1 text-center text-2xs leading-tight text-white`}
              >
                {mediaAttachment?.error || 'Error'}
              </div>
            )}
          </>
        )}

        {showRemoveButton && onRemove && (
          <button
            type="button"
            data-app-region="none"
            onClick={handleRemove}
            className={cn(
              'absolute top-1 right-1 flex aspect-square shrink-0 cursor-pointer items-center justify-center rounded-full bg-black/50 p-1 backdrop-blur-sm transition hover:bg-black',
              REMOVE_BUTTON_SIZE_CLASSES[removeButtonSize] ?? REMOVE_BUTTON_SIZE_CLASSES.large,
            )}
            aria-label={removeButtonLabel}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path
                d="M7.33464 0.666748L0.667969 7.33341M0.667969 0.666748L7.33464 7.33341"
                stroke="white"
                style={{ stroke: 'white', strokeOpacity: 1 }}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      {isTooltipOpen && (
        <DarkTooltipPortal setFloating={refs.setFloating} floatingStyles={floatingStyles} floatingProps={getFloatingProps()}>
          <span className="inline-block max-w-64 cursor-move truncate px-2 py-1 text-xs text-white">{fileName}</span>
        </DarkTooltipPortal>
      )}
    </>
  );
};

export default MediaAttachmentUploadPreview;
